import { useEffect, useState } from "react";
import { Star, ArrowRight, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";

function Stars({ rating }) {
  return (
    <div className="flex items-center gap-1" aria-label={`${rating} von 5 Sternen`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={16}
          strokeWidth={1.5}
          className={
            n <= rating
              ? "fill-[hsl(var(--brand-gold))] text-[hsl(var(--brand-gold))]"
              : "text-[hsl(var(--muted-foreground))]/40"
          }
        />
      ))}
    </div>
  );
}

export default function Reviews() {
  const { t } = useLang();
  const [reviews, setReviews] = useState([]);
  const [settings, setSettings] = useState({ google_reviews_url: "", google_write_review_url: "" });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [{ data: rev }, { data: st }] = await Promise.all([
          api.get("/reviews"),
          api.get("/settings/public"),
        ]);
        if (!cancelled) {
          setReviews(Array.isArray(rev) ? rev.slice(0, 3) : []);
          setSettings(st || {});
        }
      } catch (e) {
        // silently ignore; keep section hidden
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!loaded) return null;
  if (!reviews || reviews.length === 0) return null;

  const viewUrl = settings.google_reviews_url;
  const writeUrl = settings.google_write_review_url;

  return (
    <section
      id="rezensionen"
      data-testid="reviews-section"
      className="scroll-mt-24 bg-[hsl(var(--background))] py-24 md:py-32"
    >
      <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
        <div className="max-w-3xl mb-14 md:mb-16">
          <p className="eyebrow">{t.reviews.eyebrow}</p>
          <h2 className="font-display text-4xl md:text-6xl mt-3 leading-tight">
            {t.reviews.title}
          </h2>
        </div>

        <div className="grid gap-6 md:gap-8 md:grid-cols-3">
          {reviews.map((r) => (
            <article
              key={r.id}
              data-testid={`review-card-${r.id}`}
              className="flex flex-col p-8 md:p-9 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-sm"
            >
              <Stars rating={r.rating} />
              <p className="font-display text-lg md:text-xl mt-5 leading-relaxed text-[hsl(var(--foreground))]">
                „{r.text}"
              </p>
              <div className="mt-auto pt-6">
                <p className="text-sm font-semibold text-[hsl(var(--foreground))]">
                  {r.name}
                </p>
                <div className="flex items-center gap-2 mt-1 text-xs text-[hsl(var(--muted-foreground))]">
                  {r.date && <span>{r.date}</span>}
                  {r.date && <span aria-hidden="true">·</span>}
                  <span className="tracking-[0.18em] uppercase font-semibold text-[hsl(var(--primary))]">
                    {t.reviews.google_label}
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>

        {(viewUrl || writeUrl) && (
          <div className="mt-12 md:mt-16 flex flex-col sm:flex-row items-center justify-center gap-4">
            {viewUrl && (
              <a
                href={viewUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="reviews-view-all"
                className="inline-flex items-center gap-2 px-6 py-3 text-xs tracking-[0.22em] uppercase font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--accent))] pill"
              >
                {t.reviews.view_all} <ArrowRight size={14} />
              </a>
            )}
            {writeUrl && (
              <a
                href={writeUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="reviews-write"
                className="inline-flex items-center gap-2 px-6 py-3 text-xs tracking-[0.22em] uppercase font-semibold border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-[hsl(var(--primary-foreground))] pill transition-colors"
              >
                {t.reviews.write} <ExternalLink size={14} />
              </a>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
